import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../lib/ui";
import { ValidationTrail } from "../components/ValidationTrail";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { ArrowLeft, Save, Loader2, Download, CheckCircle2, BadgeCheck } from "lucide-react";

const CONS_ROLES = ["provincial_supervisor", "admin"];

export default function ConsolidatedView() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [texts, setTexts] = useState({});
  const [title, setTitle] = useState("");
  const [sig, setSig] = useState("");
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const res = await api.get(`/harmonized/consolidation/${id}`, { params: { lang } });
      setR(res.data);
      setTitle(res.data.title || "");
      setSig(res.data.signed_by || user?.name || "");
      const m = {};
      (res.data.pillars || []).forEach((p) => (p.narratives || []).forEach((n) => { m[`${p.volet}:${n.code}`] = n.text || ""; }));
      setTexts(m);
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, lang]);

  const save = async () => {
    setBusy("save");
    try {
      await api.put(`/harmonized/consolidation/${id}`, { narratives: texts, title });
      await load();
      toast.success(t("Modifications enregistrées."));
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const sign = async () => {
    setBusy("sign");
    try {
      await api.put(`/harmonized/consolidation/${id}`, { narratives: texts, title });
      await api.post(`/harmonized/consolidation/${id}/sign`, { signature: sig });
      await load();
      toast.success(t("Rapport provincial validé et signé."));
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const downloadPdf = async () => {
    setBusy("pdf");
    try {
      const res = await api.get(`/harmonized/consolidation/${id}/pdf`, { responseType: "blob", params: { lang } });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Consolide_${r.province}_${r.period_key}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (!r) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#F05A28]" /></div>;
  const canEdit = CONS_ROLES.includes(user?.role);

  return (
    <div data-testid="consolidated-view-page">
      <button onClick={() => navigate("/consolidated")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3" data-testid="cv-back">
        <ArrowLeft className="h-4 w-4" /> {t("Retour")}
      </button>
      <PageHeader eyebrow={t("Rapport provincial consolidé")} title={r.title}
        subtitle={`${r.province} · ${r.period_label} · ${r.reports_count} ${t("rapport(s) fusionné(s)")}`} testid="cv-header">
        <Button variant="outline" className="rounded-md" onClick={downloadPdf} disabled={!!busy} data-testid="cv-pdf">
          {busy === "pdf" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}{t("Télécharger PDF")}
        </Button>
        {canEdit && (
          <Button className="rounded-md bg-[#0F172A] hover:bg-[#1E293B]" onClick={save} disabled={!!busy} data-testid="cv-save">
            {busy === "save" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}{t("Enregistrer")}
          </Button>
        )}
      </PageHeader>

      {r.status === "signed" && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" data-testid="cv-signed-banner">
          <BadgeCheck className="h-4 w-4" /> {t("Validé et signé par")} <strong>{r.signed_by}</strong>
        </div>
      )}

      <section className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-5 mb-4" data-testid="cv-members">
        <h2 className="text-sm font-semibold text-slate-900 mb-4">{t("Rapports sources")}</h2>
        <div className="overflow-x-auto rounded-md border border-slate-200">
          <table className="w-full text-sm">
            <thead><tr className="bg-[#F1F5F9] text-left">
              {["Auteur", "Zone de santé", "Pilier", "Saisies", "Validé par"].map((h) => (
                <th key={h} className="p-2.5 text-xs font-bold text-slate-600">{t(h)}</th>))}
            </tr></thead>
            <tbody>
              {(r.members || []).map((m) => (
                <tr key={m.id} className="border-t border-slate-100">
                  <td className="p-2.5 font-medium text-slate-800">{m.author_name}</td>
                  <td className="p-2.5 text-slate-600">{m.health_zone || "-"}</td>
                  <td className="p-2.5 text-slate-600">{m.pillar_label}</td>
                  <td className="p-2.5 text-slate-600">{m.entries_count}</td>
                  <td className="p-2.5 text-slate-600">{m.signed_by || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {(r.pillars || []).map((p) => (
        <section key={p.volet} className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-5 mb-4" data-testid={`cv-pillar-${p.volet}`}>
          <h2 className="text-sm font-semibold text-slate-900 mb-4">{p.label}</h2>
          <div className="overflow-x-auto rounded-md border border-slate-200 mb-5">
            <table className="w-full text-sm">
              <thead><tr className="bg-[#F1F5F9] text-left">
                <th className="p-2.5 text-xs font-bold text-slate-600">{t("Indicateur")}</th>
                <th className="p-2.5 text-xs font-bold text-slate-600">{t("Total provincial")}</th>
              </tr></thead>
              <tbody>
                {(p.indicators || []).filter((x) => x.current).map((x) => (
                  <tr key={x.code} className="border-t border-slate-100">
                    <td className="p-2.5 text-slate-700">{x.label}</td>
                    <td className="p-2.5 font-semibold text-slate-900">{x.current}</td>
                  </tr>
                ))}
                {(p.indicators || []).filter((x) => x.current).length === 0 && (
                  <tr><td colSpan={2} className="p-4 text-center text-xs text-slate-500">{t("Aucune valeur agrégée.")}</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="space-y-5">
            {(p.narratives || []).map((n) => (
              <div key={n.code}>
                <Label className="text-sm font-semibold text-slate-800">{n.label}</Label>
                <Textarea rows={4} className="mt-1.5" value={texts[`${p.volet}:${n.code}`] || ""} readOnly={!canEdit}
                  onChange={(e) => setTexts({ ...texts, [`${p.volet}:${n.code}`]: e.target.value })}
                  data-testid={`cv-narr-${p.volet}-${n.code}`} />
              </div>
            ))}
          </div>
        </section>
      ))}

      {CONS_ROLES.includes(user?.role) && <ValidationTrail history={r.history} />}

      {canEdit && (
        <section className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-5" data-testid="cv-sign">
          <h2 className="text-sm font-semibold text-slate-900 mb-4">{t("Validation du rapport provincial")}</h2>
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="sm:w-80">
              <Label className="text-xs">{t("Titre du rapport")}</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="cv-title" />
            </div>
            <div className="sm:w-72">
              <Label className="text-xs">{t("Nom et prénom du validateur")}</Label>
              <Input value={sig} onChange={(e) => setSig(e.target.value)} data-testid="cv-signature" />
            </div>
            <Button onClick={sign} disabled={!!busy} className="rounded-md bg-[#F05A28] hover:bg-[#D84A1B]" data-testid="cv-sign-btn">
              {busy === "sign" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
              {t(r.status === "signed" ? "Mettre à jour la signature" : "Valider et signer")}
            </Button>
          </div>
        </section>
      )}
    </div>
  );
}
